import React, { useState, useEffect } from 'react';
import { 
  Share2, Package, TrendingUp, DollarSign, Eye, MousePointer,
  Plus, Search, Filter, ExternalLink, Copy, ShoppingCart, Star
} from 'lucide-react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { useAuth } from '../../context/AuthContext';

const AffiliateMarketplace = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('browse');
  const [products, setProducts] = useState([]);
  const [myProducts, setMyProducts] = useState([]);
  const [earnings, setEarnings] = useState(null);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);

  useEffect(() => {
    fetchData();
  }, [activeTab, searchQuery, selectedCategory]);

  const fetchData = async () => {
    setLoading(true);
    try {
      if (activeTab === 'browse') {
        const res = await api.get(`/affiliate/products?search=${searchQuery}&category=${selectedCategory}`);
        setProducts(res.data.products);
        setCategories(res.data.categories);
      } else if (activeTab === 'my-products') {
        const res = await api.get('/affiliate/my-products');
        setMyProducts(res.data);
      } else if (activeTab === 'earnings') {
        const res = await api.get('/affiliate/my-earnings');
        setEarnings(res.data);
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const generateAffiliateLink = async (productId) => {
    try {
      const res = await api.get(`/affiliate/generate-link/${productId}`);
      navigator.clipboard.writeText(window.location.origin + res.data.affiliate_link);
      toast.success(`Link copied! Commission: ${res.data.commission}`);
    } catch (error) {
      toast.error('Failed to generate link');
    }
  };

  const trackClick = async (productId) => {
    try {
      await api.post('/affiliate/track-click', {
        product_id: productId,
        referrer_id: user?.id
      });
    } catch (error) {
      // Silent fail for tracking
    }
  };

  return (
    <div className="min-h-screen pt-24 pb-16 px-6" data-testid="affiliate-marketplace">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-3">
              <Share2 className="w-8 h-8 text-primary" />
              Affiliate Marketplace
            </h1>
            <p className="text-muted-foreground">Promote products & earn commissions</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 border-b border-white/10 mb-6 overflow-x-auto">
          {[
            { id: 'browse', label: 'Browse Products', icon: Package },
            { id: 'my-products', label: 'My Products', icon: Star },
            { id: 'earnings', label: 'My Earnings', icon: DollarSign }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2 flex items-center gap-2 whitespace-nowrap transition-colors ${
                activeTab === tab.id 
                  ? 'text-primary border-b-2 border-primary' 
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Browse Products Tab */}
        {activeTab === 'browse' && (
          <div>
            {/* Search & Filters */}
            <div className="flex flex-col md:flex-row gap-4 mb-6">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <input
                  type="text"
                  placeholder="Search products..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 bg-white/5 border border-white/10 rounded-lg"
                />
              </div>
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="px-4 py-2 bg-white/5 border border-white/10 rounded-lg"
              >
                <option value="">All Categories</option>
                {categories.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>

            {/* Products Grid */}
            {loading ? (
              <div className="flex justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-primary"></div>
              </div>
            ) : products.length > 0 ? (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {products.map(product => (
                  <ProductCard 
                    key={product.id} 
                    product={product} 
                    onGenerateLink={() => generateAffiliateLink(product.id)}
                    onTrackClick={() => trackClick(product.id)}
                  />
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <Package className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-xl font-semibold mb-2">No Products Found</h3>
                <p className="text-muted-foreground">Check back later for new affiliate products</p>
              </div>
            )}
          </div>
        )}

        {/* My Products Tab */}
        {activeTab === 'my-products' && (
          <div>
            <div className="flex justify-between items-center mb-6">
              <p className="text-muted-foreground">{myProducts.length} products listed</p>
              <button
                onClick={() => setShowCreateModal(true)}
                className="btn-primary flex items-center gap-2"
              >
                <Plus className="w-4 h-4" />
                List New Product
              </button>
            </div>

            {loading ? (
              <div className="flex justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-primary"></div>
              </div>
            ) : myProducts.length > 0 ? (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {myProducts.map(product => (
                  <div key={product.id} className="glass-card p-4">
                    {product.images?.[0] && (
                      <img src={product.images[0]} alt="" className="w-full h-40 object-cover rounded-lg mb-4" />
                    )}
                    <h3 className="font-semibold mb-1">{product.name}</h3>
                    <p className="text-primary font-bold mb-2">₹{product.price}</p>
                    <div className="flex justify-between text-sm text-muted-foreground mb-4">
                      <span>{product.views || 0} views</span>
                      <span>{product.clicks || 0} clicks</span>
                      <span>{product.sales || 0} sales</span>
                    </div>
                    <span className={`px-2 py-1 rounded-full text-xs ${
                      product.status === 'approved' ? 'bg-green-500/20 text-green-500' :
                      product.status === 'pending' ? 'bg-yellow-500/20 text-yellow-500' :
                      'bg-red-500/20 text-red-500'
                    }`}>
                      {product.status}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <Package className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-xl font-semibold mb-2">No Products Listed</h3>
                <p className="text-muted-foreground mb-4">Start earning by listing your products</p>
                <button onClick={() => setShowCreateModal(true)} className="btn-primary">
                  List Your First Product
                </button>
              </div>
            )}
          </div>
        )}

        {/* Earnings Tab */}
        {activeTab === 'earnings' && earnings && (
          <div>
            {/* Stats Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              <StatCard icon={DollarSign} label="Total Earnings" value={`₹${earnings.total_earnings}`} color="primary" />
              <StatCard icon={DollarSign} label="Pending" value={`₹${earnings.pending_earnings}`} color="yellow" />
              <StatCard icon={DollarSign} label="Paid" value={`₹${earnings.paid_earnings}`} color="green" />
              <StatCard icon={TrendingUp} label="Conversion Rate" value={`${earnings.conversion_rate}%`} color="blue" />
            </div>

            {/* Stats Row */}
            <div className="grid md:grid-cols-2 gap-4 mb-8">
              <div className="glass-card p-6">
                <h3 className="font-semibold mb-4">Performance</h3>
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <span className="flex items-center gap-2 text-muted-foreground">
                      <MousePointer className="w-4 h-4" /> Total Clicks
                    </span>
                    <span className="font-bold">{earnings.total_clicks}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="flex items-center gap-2 text-muted-foreground">
                      <ShoppingCart className="w-4 h-4" /> Conversions
                    </span>
                    <span className="font-bold">{earnings.total_conversions}</span>
                  </div>
                </div>
              </div>
              
              <div className="glass-card p-6">
                <h3 className="font-semibold mb-4">Quick Tips</h3>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li>• Share affiliate links on social media</li>
                  <li>• Create content around products you promote</li>
                  <li>• Focus on high-commission products</li>
                  <li>• Track which products convert best</li>
                </ul>
              </div>
            </div>

            {/* Recent Sales */}
            {earnings.recent_sales?.length > 0 && (
              <div className="glass-card p-6">
                <h3 className="font-semibold mb-4">Recent Sales</h3>
                <div className="space-y-3">
                  {earnings.recent_sales.map((sale, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                      <div>
                        <p className="font-medium">{sale.product_name}</p>
                        <p className="text-xs text-muted-foreground">
                          {new Date(sale.created_at).toLocaleDateString()}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-green-500 font-bold">+₹{sale.commission_amount}</p>
                        <span className={`text-xs px-2 py-0.5 rounded ${
                          sale.status === 'paid' ? 'bg-green-500/20 text-green-500' : 'bg-yellow-500/20 text-yellow-500'
                        }`}>
                          {sale.status}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Create Product Modal */}
        {showCreateModal && (
          <CreateProductModal 
            onClose={() => setShowCreateModal(false)}
            onSuccess={() => {
              setShowCreateModal(false);
              fetchData();
            }}
          />
        )}
      </div>
    </div>
  );
};

// Product Card Component
const ProductCard = ({ product, onGenerateLink, onTrackClick }) => {
  return (
    <div className="glass-card p-4 group">
      {product.images?.[0] && (
        <img 
          src={product.images[0]} 
          alt={product.name}
          className="w-full h-40 object-cover rounded-lg mb-4"
        />
      )}
      <div className="mb-2">
        <span className="px-2 py-0.5 bg-white/10 rounded text-xs">{product.category}</span>
      </div>
      <h3 className="font-semibold mb-1 line-clamp-2">{product.name}</h3>
      <p className="text-sm text-muted-foreground line-clamp-2 mb-3">{product.description}</p>
      
      <div className="flex items-center justify-between mb-4">
        <div>
          <p className="text-xl font-bold text-primary">₹{product.price}</p>
          {product.original_price > product.price && (
            <p className="text-sm text-muted-foreground line-through">₹{product.original_price}</p>
          )}
        </div>
        <div className="text-right">
          <p className="text-green-500 font-semibold">
            {product.commission_type === 'percentage' 
              ? `${product.commission_value}%` 
              : `₹${product.commission_value}`}
          </p>
          <p className="text-xs text-muted-foreground">Commission</p>
        </div>
      </div>

      <div className="flex gap-2">
        <button
          onClick={onGenerateLink}
          className="flex-1 flex items-center justify-center gap-1 px-3 py-2 bg-primary/20 text-primary rounded-lg hover:bg-primary/30"
        >
          <Copy className="w-4 h-4" />
          Get Link
        </button>
        {product.external_link && (
          <a
            href={product.external_link}
            target="_blank"
            rel="noopener noreferrer"
            onClick={onTrackClick}
            className="px-3 py-2 bg-white/5 rounded-lg hover:bg-white/10"
          >
            <ExternalLink className="w-4 h-4" />
          </a>
        )}
      </div>
    </div>
  );
};

// Create Product Modal
const CreateProductModal = ({ onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    original_price: '',
    commission_type: 'percentage',
    commission_value: 10,
    category: 'general',
    external_link: '',
    is_digital: true
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    if (!formData.name || !formData.price) {
      toast.error('Please fill in required fields');
      return;
    }
    
    setLoading(true);
    try {
      await api.post('/affiliate/products/create', {
        ...formData,
        price: parseFloat(formData.price),
        original_price: formData.original_price ? parseFloat(formData.original_price) : null,
        commission_value: parseFloat(formData.commission_value)
      });
      toast.success('Product submitted for approval!');
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create product');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center p-6">
      <div className="glass-card p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <h3 className="text-xl font-bold mb-6">List New Product</h3>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2">Product Name *</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({...formData, name: e.target.value})}
              className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-2">Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({...formData, description: e.target.value})}
              className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg"
              rows={3}
            />
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">Price (₹) *</label>
              <input
                type="number"
                value={formData.price}
                onChange={(e) => setFormData({...formData, price: e.target.value})}
                className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Original Price</label>
              <input
                type="number"
                value={formData.original_price}
                onChange={(e) => setFormData({...formData, original_price: e.target.value})}
                className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg"
              />
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">Commission Type</label>
              <select
                value={formData.commission_type}
                onChange={(e) => setFormData({...formData, commission_type: e.target.value})}
                className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg"
              >
                <option value="percentage">Percentage</option>
                <option value="fixed">Fixed Amount</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Commission Value</label>
              <input
                type="number"
                value={formData.commission_value}
                onChange={(e) => setFormData({...formData, commission_value: e.target.value})}
                className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg"
              />
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-2">Category</label>
            <input
              type="text"
              value={formData.category}
              onChange={(e) => setFormData({...formData, category: e.target.value})}
              className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-2">External Link (Optional)</label>
            <input
              type="url"
              value={formData.external_link}
              onChange={(e) => setFormData({...formData, external_link: e.target.value})}
              className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg"
              placeholder="https://..."
            />
          </div>
        </div>
        
        <div className="flex gap-3 mt-6">
          <button
            onClick={handleSubmit}
            disabled={loading}
            className="flex-1 btn-primary disabled:opacity-50"
          >
            {loading ? 'Submitting...' : 'Submit for Approval'}
          </button>
          <button onClick={onClose} className="flex-1 px-4 py-2 bg-white/5 rounded-full">
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

const StatCard = ({ icon: Icon, label, value, color }) => {
  const colorClasses = {
    primary: 'text-primary bg-primary/20',
    yellow: 'text-yellow-500 bg-yellow-500/20',
    green: 'text-green-500 bg-green-500/20',
    blue: 'text-blue-500 bg-blue-500/20'
  };

  return (
    <div className="glass-card p-4">
      <div className="flex items-center gap-3">
        <div className={`p-3 rounded-xl ${colorClasses[color]}`}>
          <Icon className="w-6 h-6" />
        </div>
        <div>
          <p className="text-2xl font-bold">{value}</p>
          <p className="text-sm text-muted-foreground">{label}</p>
        </div>
      </div>
    </div>
  );
};

export default AffiliateMarketplace;
